import { useEffect, useState } from 'react';
import { Col } from 'reactstrap';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin, { Draggable } from '@fullcalendar/interaction';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';

import { EventClick } from './EventClick';

import { baseLang } from '../../../../lib/utils';
import { UserRole } from '../../../../types/Forms';
import { CalendarData } from '../../../../types/Applications';
import { useTranslation } from 'react-i18next';
import ukLocale from '@fullcalendar/core/locales/uk';
import ruLocale from '@fullcalendar/core/locales/ru';
import enGbLocale from '@fullcalendar/core/locales/en-gb';
import { LocaleInput } from '@fullcalendar/core';
import { useNavigate } from 'react-router-dom';
import Loader from '../../../../layout/Loader';
import { CustomLoader } from '../../../uiKits/customLoader/CustomLoader';

interface DragCalendarProps {
  calendarData: CalendarData[];
  role: UserRole;
  actionHandler?: (d: any) => void;
  openAddForm?: (d: any) => void;
  closeAddForm?: () => void;
  examDetailsGetter?: (id: number) => void;
  initialDate?: Date;
  onMonthChange?: (d: Date) => void;
  loading?: boolean;
  refetching?: boolean;
}
//The component will display the calendar events
const DragCalendar = ({
  calendarData,
  role,
  actionHandler,
  openAddForm,
  closeAddForm,
  examDetailsGetter,
  initialDate,
  onMonthChange,
  loading,
  refetching,
}: DragCalendarProps) => {
  const { i18n, t } = useTranslation();
  const localeMap: Record<string, LocaleInput> = {
    uk: ukLocale,
    ru: ruLocale,
    en: enGbLocale,
  };
  const navigate = useNavigate();
  const examLanguage = (lang: string | null) => {
    if (!lang) return t('courses.exam.examLangNotSelected');
    if (lang === 'uk') return t('courses.exam.examLangUkrainian');
    if (lang === 'ru') return t('courses.exam.examLangRussian');
    return t('courses.exam.examLangNotSelected');
  };
  console.log('Calendar render', { calendarData });
  const pageLang = baseLang(i18n.language);
  return (
    <>
      {/* <EventLists /> */}
      <Col className="box-col-12 calendar-wrap">
        {loading ? (
          <CustomLoader />
        ) : (
          <FullCalendar
            locale={localeMap[baseLang(pageLang)] ?? enGbLocale}
            initialView="dayGridMonth"
            initialDate={initialDate}
            headerToolbar={{
              left: 'prev,next today',
              center: 'title',
              right: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek',
            }}
            firstDay={1}
            weekends={false}
            rerenderDelay={10}
            eventDurationEditable={false}
            editable={false}
            droppable={false}
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, timeGridPlugin]}
            datesSet={(arg) => {
              const anchor = arg.view.currentStart;
              onMonthChange?.(anchor);
            }}
            dayMaxEventRows={true}
            events={calendarData.map((event) => {
              const translatedLesson = event.lessonTranslations?.find((tr) => tr.lang === pageLang);
              const translatedCourse = event.extendedProps.courseTranslations?.find(
                (tr) => tr.lang === pageLang,
              );
              const translatedCategory = event.extendedProps.categoryTranslations?.find(
                (tr) => tr.lang === pageLang,
              );

              // normalize + pack everything into extendedProps
              const extendedProps = {
                ...event.extendedProps,

                // normalized meet link: prefer googleMeetLink, else gMeetLink, else null
                googleMeetLink: event.extendedProps.googleMeetLink ?? null,

                // translated labels
                description:
                  pageLang === 'en'
                    ? event.extendedProps.description
                    : (translatedLesson?.description ?? event.extendedProps.description),

                courseTitle:
                  pageLang === 'en'
                    ? event.extendedProps.courseTitle
                    : (translatedCourse?.name ?? event.extendedProps.courseTitle),

                category:
                  pageLang === 'en'
                    ? event.extendedProps.category
                    : (translatedCategory?.name ?? event.extendedProps.category),

                // human-readable exam language
                examLang: examLanguage(event.extendedProps.examLang),

                // keep these available in popup
                userName: event.extendedProps.userName,
                email: event.extendedProps.email,
                lessonTranslations: event.lessonTranslations,
                courseTranslations: event.extendedProps.courseTranslations,
                categoryTranslations: event.extendedProps.categoryTranslations,

                actionHandler: actionHandler ? actionHandler : null,
                openAddForm: openAddForm ? openAddForm : null,
                closeAddForm: closeAddForm ? closeAddForm : null,

                lessonId: event.extendedProps.lessonId,
                courseId: event.extendedProps.courseId,
              };

              return {
                id: String(event.id),
                title: pageLang === 'en' ? event.title : (translatedLesson?.title ?? event.title),
                start: event.start,
                // IMPORTANT: put all custom stuff here
                extendedProps,
              };
            })}
            eventClick={(event) => {
              EventClick(event, role ?? UserRole.STUDENT, t, (to: string) => navigate(to));
            }}
            eventDidMount={(info) => {
              if (
                info.event.extendedProps.assistant.isAppointed &&
                !info.event.extendedProps.assistant.isPassed
              ) {
                const dot = info.el.querySelector<HTMLElement>('.fc-daygrid-event-dot');
                if (dot) {
                  dot.style.borderColor = 'yellow';
                }
              }
              if (
                info.event.extendedProps.assistant.isAppointed &&
                info.event.extendedProps.assistant.isPassed
              ) {
                const dot = info.el.querySelector<HTMLElement>('.fc-daygrid-event-dot');
                if (dot) {
                  dot.style.borderColor = 'green';
                }
              }
            }}
            eventClassNames={(info) => {
              const a = info.event.extendedProps?.assistant ?? {};
              if (a.isAppointed && a.isPassed) return ['evt--passed'];
              if (a.isAppointed && !a.isPassed) return ['evt--appointed'];
              return [];
            }}
          />
        )}
      </Col>
    </>
  );
};

export default DragCalendar;
